import { useState, useEffect, useCallback } from 'react';
import { adminApi, authApi } from '@/services/authApi';
import { Icon } from './memberIcons';
import m from './Member.module.scss';

const CAT: Record<string, string> = { PRODUCT: '제품·솔루션', INSTALL_AS: '설치·A/S', QUOTE: '견적·도입', DEMO: '체험·데모', ETC: '기타' };
interface Row { id: number; user_id: number; category: string; title: string; status: string; contact_name: string; contact_phone: string; created_at: string; }
interface Detail { id: number; category: string; title: string; content: string; status: string; answer?: string | null; contact_name?: string; contact_phone?: string; contact_email?: string; created_at: string; attach_name?: string | null; }

/** [2026-09-10 / 2026-09-11 개편] 관리자 문의 게시판 — solution-partners 밝은 테마로 통일(기존 다크에서 변경).
 *   검색·카테고리·상태 필터·페이징 + 상세·답변. AdminHome 문의관리 탭에서 사용. */
export default function AdminInquiries() {
	const [rows, setRows] = useState<Row[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [kw, setKw] = useState('');
	const [q, setQ] = useState('');
	const [cat, setCat] = useState('');
	const [status, setStatus] = useState('');
	const [open, setOpen] = useState<Detail | null>(null);
	const [answer, setAnswer] = useState('');
	const [msg, setMsg] = useState<string | null>(null);
	const size = 20;

	const load = useCallback(async () => {
		const r = await adminApi.inquiries(q, cat, status, page, size);
		if (r.success) { setRows((r.items as Row[]) || []); setTotal(Number(r.total) || 0); }
	}, [q, cat, status, page]);
	useEffect(() => { load(); }, [load]);

	async function openDetail(id: number) {
		const r = await authApi.inquiryDetail(id);
		if (r.success) { setOpen(r.inquiry as Detail); setAnswer(((r.inquiry as Detail).answer) || ''); setMsg(null); }
	}
	async function submitAnswer() {
		if (!open) return;
		if (!answer.trim()) { setMsg('답변 내용을 입력해주세요.'); return; }
		const r = await adminApi.answerInquiry(open.id, answer.trim());
		setMsg(r.message || null);
		if (r.success) { setOpen(null); load(); }
	}
	const totalPages = Math.max(1, Math.ceil(total / size));

	if (open) {
		return (
			<div className={m.card}>
				<p className={m.eyebrow} style={{ marginBottom: 8 }}>{CAT[open.category] || open.category} · {open.status === 'ANSWERED' ? '답변완료' : '답변대기'}</p>
				<h2 className={m.h2} style={{ marginBottom: 6 }}>{open.title}</h2>
				<p className={m.lead} style={{ marginBottom: 16 }}>
					{open.contact_name} · {open.contact_phone} · {open.contact_email || '-'} · {String(open.created_at).slice(0, 16).replace('T', ' ')}
				</p>
				<p style={{ color: '#333a42', whiteSpace: 'pre-wrap', lineHeight: 1.7, background: '#f7f9fb', border: '1px solid #e6e9ee', borderRadius: 10, padding: 16, margin: '0 0 12px' }}>{open.content}</p>
				{open.attach_name && (
					<button className={`${m.btn} ${m.btnGhost}`} style={{ marginBottom: 20 }}
						onClick={() => authApi.downloadInquiryAttachment(open.id, open.attach_name!)}>📎 {open.attach_name}</button>
				)}
				<label className={m.label}>답변 <span style={{ float: 'right', color: '#9aa4af', fontWeight: 500 }}>{answer.length}/2000</span></label>
				<textarea className={m.input} style={{ width: '100%', height: 140, padding: 12, resize: 'vertical', lineHeight: 1.6 }}
					value={answer} maxLength={2000} onChange={(e) => setAnswer(e.target.value.slice(0, 2000))} placeholder="답변 내용을 입력하세요." />
				{msg && <p className={`${m.hint} ${m.hintBad}`}>{msg}</p>}
				<div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
					<button className={m.btn} style={{ flex: 1, justifyContent: 'center' }} onClick={submitAnswer}>{open.status === 'ANSWERED' ? '답변 수정' : '답변 등록'}</button>
					<button className={`${m.btn} ${m.btnGhost}`} onClick={() => setOpen(null)}>목록</button>
				</div>
			</div>
		);
	}

	return (
		<>
			<p className={m.eyebrow}>INQUIRIES</p>
			<h2 className={m.h2}>문의 <b>관리</b></h2>
			<p className={m.lead}>제품·설치·견적·A/S·체험 문의를 확인하고 답변합니다.</p>

			<div className={m.toolbar}>
				<select className={m.input} style={{ flex: '0 0 auto', minWidth: 150 }} value={cat} onChange={(e) => { setCat(e.target.value); setPage(0); }}>
					<option value="">전체 유형</option>
					{Object.entries(CAT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
				</select>
				<select className={m.input} style={{ flex: '0 0 auto', minWidth: 130 }} value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
					<option value="">전체 상태</option>
					<option value="PENDING">답변대기</option>
					<option value="ANSWERED">답변완료</option>
				</select>
				<input className={m.input} value={kw} onChange={(e) => setKw(e.target.value)} placeholder="제목·이름·전화 검색"
					onKeyDown={(e) => { if (e.key === 'Enter') { setPage(0); setQ(kw.trim()); } }} />
				<button className={m.btn} onClick={() => { setPage(0); setQ(kw.trim()); }}><Icon.search />검색</button>
			</div>

			<div className={m.tableWrap}>
				<table className={m.table} style={{ minWidth: 720 }}>
					<thead><tr><th>ID</th><th>유형</th><th>제목</th><th>이름</th><th>전화</th><th>상태</th><th>등록일</th></tr></thead>
					<tbody>
						{rows.length === 0 ? <tr><td colSpan={7} className={m.empty}>문의가 없습니다.</td></tr>
							: rows.map((r) => (
								<tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(r.id)}>
									<td className={m.mono}>{r.id}</td>
									<td><span className={`${m.badge} ${m.bLocal}`}>{CAT[r.category] || r.category}</span></td>
									<td style={{ fontWeight: 600, color: '#1a1d21' }}>{r.title}</td>
									<td>{r.contact_name || '-'}</td>
									<td className={m.mono}>{r.contact_phone || '-'}</td>
									<td><span className={`${m.badge} ${r.status === 'ANSWERED' ? m.bActive : m.bPending}`}>{r.status === 'ANSWERED' ? '답변완료' : '답변대기'}</span></td>
									<td className={m.mono}>{String(r.created_at).slice(0, 10)}</td>
								</tr>
							))}
					</tbody>
				</table>
			</div>
			<div className={m.pager}>
				<button disabled={page <= 0} onClick={() => setPage((p) => p - 1)}>이전</button>
				<span>{page + 1} / {totalPages} · 총 {total}건</span>
				<button disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>다음</button>
			</div>
		</>
	);
}
